import type { TLabel } from '@shared/types/label';

import React, { useRef } from 'react';
import { Button } from '@src/design-system/button';
import { Input } from '@src/design-system/input';
import { HexColorPicker } from 'react-colorful';
import { ArrowDown } from 'lucide-react';
import { useLabelFormLogic } from '../hooks/use-label-form-logic';
import { PopoverClose } from '@src/design-system/popover';
import { cn } from '@src/design-system/helpers';

const StaticPickedColors = [
  '#EF4444',
  '#F97316',
  '#FACC15',
  '#10B981',
  '#14B8A6',
  '#0EA5E9',
  '#6366F1',
  '#8B5CF6',
  '#EC4899',
  '#22C55E',
  '#F43F5E',
  '#FB7185',
  '#3B82F6',
  '#06B6D4',
];
type LabelColorPickerProps = {
  data?: TLabel;
};

export const LabelColorPicker: React.FC<LabelColorPickerProps> = ({ data }) => {
  const {
    label,
    setLabel,
    color,
    handleColor,
    isColorOpen,
    setIsColorOpen,
    errors,
    handleSave,
  } = useLabelFormLogic(data);
  const popoverRef = useRef<HTMLButtonElement>(null);
  const handleSubmit = async () => {
    try {
      await handleSave();
      if (popoverRef.current) popoverRef.current.click();
    } catch (err) {
      return err;
    }
  };

  return (
    <div className='space-y-3 bg-white rounded-lg p-3'>
      <div className='flex items-center justify-between gap-2'>
        <Button
          onClick={() => setIsColorOpen(!isColorOpen)}
          variant='primaryOutline'
          size='iconSm'
          className='bg-primary-100'
        >
          <ArrowDown className={cn(isColorOpen ? 'rotate-180' : '')} />
        </Button>

        <Input
          type='text'
          size='sm'
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder='Enter Label Name'
          className={cn('flex-1', errors?.label && 'border-red-500')}
          inputClassName='text-sm placeholder:text-[11px]'
        />

        <Button
          onClick={handleSubmit}
          variant='primary'
          size='iconSm'
        >
          ✔
        </Button>
      </div>

      {isColorOpen && (
        <>
          <HexColorPicker
            color={color}
            onChange={(e) => handleColor(e)}
            style={{ width: '100%', height: '160px' }}
          />
          <div className='flex items-center gap-2 mt-2'>
            <Input
              type='text'
              value={'Hex'}
              disabled
              className='w-28 px-2 py-1 border rounded-md text-sm'
            />
            <Input
              type='text'
              value={color.toUpperCase()}
              onChange={(e) => handleColor(e.target.value)}
              className={`w-28 px-2 py-1 border rounded-md text-sm ${errors?.color ? 'border-red-500' : ''}`}
            />
          </div>
          <div className='flex flex-wrap gap-2 mt-2'>
            <p className='text-gray-800 my-2 text-sm'>Most picked colors</p>
            <div className='flex flex-wrap gap-2'>
              {StaticPickedColors.map((c) => (
                <button
                  key={c}
                  style={{ backgroundColor: c }}
                  className={`w-6 h-6 rounded-full border-2 ${
                    c === color ? 'border-black' : 'border-transparent'
                  }`}
                  onClick={() => handleColor(c)}
                  type='button'
                />
              ))}
            </div>
          </div>
        </>
      )}

      <PopoverClose
        ref={popoverRef}
        className='hidden'
      />
    </div>
  );
};
